import { useLingui as _useLingui } from "../../lib/i18n/useLingui";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuPortal } from "../../components/Dropdown";
import { Icon } from "../../components/Icon";
import { Tooltip } from "../../components/Tooltip";
import { cn } from "../../lib/utils";
import React, { useState } from "react";
import { DENSITY_HEADER_HEIGHT_CLASS, SCROLL_COLUMN_MIN_WIDTH_CLASS, useDataTableLayout } from "./layout";
import { renderMenuItem } from "./menu";
import { ALIGN_JUSTIFY_CLASS, ALIGN_TEXT_CLASS, getDataTableColumnPresets, getSortIcon } from "./presets";
/** The underlying table element with its container-query wrapper. */
export function Root({
  children,
  className,
  containerClassName,
  containerProps,
  containerRef,
  ...props
}) {
  return /*#__PURE__*/React.createElement("div", {
    ref: containerRef,
    className: cn("@container/table", containerClassName),
    ...containerProps
  }, /*#__PURE__*/React.createElement("table", {
    className: cn("w-full table-fixed border-collapse", className),
    ...props
  }, children));
}
/** Table head section (thead). */
export function Header({
  children,
  className,
  ...props
}) {
  return /*#__PURE__*/React.createElement("thead", {
    className: cn(className),
    ...props
  }, children);
}
/** Header cell (th) with alignment, optional tooltip, and optional sort button from the column meta. */
/**
 * @cc [owner:aubin-tchoi,label:react] stable-sort-header
 * Pressing a sort header MUST NOT move or resize its label or icon.
 */
export function Head({
  children,
  className,
  column,
  onSort,
  ...props
}) {
  var _column$columnDef$met, _column$columnDef$met2, _column$columnDef$met3;
  const {
    _: _t
  } = _useLingui();
  const layout = useDataTableLayout();
  const presets = getDataTableColumnPresets(column);
  const sorted = column.getIsSorted();
  const hasEmptyActionsHeader = ((_column$columnDef$met = column.columnDef.meta) === null || _column$columnDef$met === void 0 ? void 0 : _column$columnDef$met.type) === "row-actions" && column.columnDef.header === "";
  const content = onSort ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: onSort,
    className: cn("heading-sm flex w-full min-w-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-xs text-foreground", "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring", ALIGN_JUSTIFY_CLASS[presets.headerAlign])
  }, typeof column.columnDef.header === "string" ? /*#__PURE__*/React.createElement("span", {
    className: "min-w-0 truncate"
  }, children) :
  // Custom header renderers lay themselves out (e.g. a centered
  // label); wrapping them would shrink `w-full` to the text width.
  children, /*#__PURE__*/React.createElement(Icon, {
    visual: getSortIcon(sorted),
    size: "xs",
    className: "shrink-0"
  })) : children;
  return /*#__PURE__*/React.createElement("th", {
    scope: "col",
    "aria-sort": onSort ? sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : "none" : undefined,
    className: cn("heading-sm px-2", DENSITY_HEADER_HEIGHT_CLASS[layout.density], ALIGN_TEXT_CLASS[presets.headerAlign], "text-foreground", layout.enforceColumnMinWidth && presets.sortable && SCROLL_COLUMN_MIN_WIDTH_CLASS, presets.headerClassName, (_column$columnDef$met2 = column.columnDef.meta) === null || _column$columnDef$met2 === void 0 ? void 0 : _column$columnDef$met2.className, className),
    ...props
  }, hasEmptyActionsHeader && /*#__PURE__*/React.createElement("span", {
    className: "sr-only"
  }, _t(
  /** i18n */
  {
    id: "7L01XJ",
    message: "Actions"
  })), (_column$columnDef$met3 = column.columnDef.meta) !== null && _column$columnDef$met3 !== void 0 && _column$columnDef$met3.tooltip ? /*#__PURE__*/React.createElement(Tooltip, {
    label: column.columnDef.meta.tooltip,
    trigger: content
    // The sort button must be the trigger itself, not nested in one.
    ,
    tooltipTriggerAsChild: onSort !== undefined
  }) : content);
}

/** Table body section (tbody). */
export function Body({
  children,
  className,
  ...props
}) {
  return /*#__PURE__*/React.createElement("tbody", {
    className: className,
    ...props
  }, children);
}
/** Table row (tr) with hover/selection styling and a right-click context menu when rowData.menuItems is set. */
export function Row({
  children,
  className,
  onClick,
  onDoubleClick,
  widthClassName,
  rowData,
  hideBottomBorder = false,
  ...props
}) {
  var _rowData$menuItems2, _rowData$menuItems3;
  const [contextMenuPosition, setContextMenuPosition] = useState(null);
  const handleContextMenu = event => {
    var _rowData$menuItems;
    if (!(rowData !== null && rowData !== void 0 && (_rowData$menuItems = rowData.menuItems) !== null && _rowData$menuItems !== void 0 && _rowData$menuItems.length)) {
      return;
    }
    event.preventDefault();
    setContextMenuPosition({
      x: event.clientX,
      y: event.clientY
    });
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("tr", {
    className: cn("group/dt-row justify-center transition-colors duration-300 ease-out", !hideBottomBorder && ["border-b", "border-separator"], (onClick || onDoubleClick) && "cursor-pointer [&:hover:not(:has(input:hover)):not(:has(button:hover))]:bg-muted-background", props["data-selected"] && "bg-muted-background/50", widthClassName, className),
    onClick: onClick || undefined,
    onDoubleClick: onDoubleClick || undefined,
    onContextMenu: handleContextMenu,
    ...props
  }, children), contextMenuPosition && (rowData === null || rowData === void 0 || (_rowData$menuItems2 = rowData.menuItems) === null || _rowData$menuItems2 === void 0 ? void 0 : _rowData$menuItems2.length) && /*#__PURE__*/React.createElement(DropdownMenu, {
    open: !!contextMenuPosition,
    onOpenChange: open => !open && setContextMenuPosition(null),
    modal: true
  }, /*#__PURE__*/React.createElement(DropdownMenuPortal, null, /*#__PURE__*/React.createElement(DropdownMenuContent, {
    align: "start",
    className: "whitespace-nowrap",
    style: {
      position: "fixed",
      left: (contextMenuPosition === null || contextMenuPosition === void 0 ? void 0 : contextMenuPosition.x) || 0,
      top: (contextMenuPosition === null || contextMenuPosition === void 0 ? void 0 : contextMenuPosition.y) || 0
    }
  }, /*#__PURE__*/React.createElement(DropdownMenuGroup, null, rowData === null || rowData === void 0 || (_rowData$menuItems3 = rowData.menuItems) === null || _rowData$menuItems3 === void 0 ? void 0 : _rowData$menuItems3.map((item, index) => renderMenuItem(item, index, () => setContextMenuPosition(null))))))));
}
//# sourceMappingURL=parts.js.map